import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import LiveSignal from "@/components/LiveSignal";

const LINES = ["A model of you", "that learns", "as you live."];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const textOpacity = useTransform(scrollYProgress, [0, 0.5, 0.8], [1, 1, 0]);
  const textY = useTransform(scrollYProgress, [0, 0.8], [0, -80]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const notesOpacity = useTransform(scrollYProgress, [0.15, 0.55], [0, 1]);
  const fadeAll = useTransform(scrollYProgress, [0.85, 1], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[165vh]">
      <motion.div
        style={{ opacity: fadeAll }}
        className="sticky top-0 flex h-screen items-center overflow-hidden"
      >
        <motion.div
          style={{ scale: imgScale, y: imgY }}
          className="absolute inset-0"
        >
          <img
            src="/assets/hero_headshot.jpg"
            alt="A person beside their computational digital twin"
            data-testid="hero-image"
            className="h-full w-full object-cover object-[85%_center]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
          <motion.div
            style={{ opacity: notesOpacity }}
            className="absolute inset-0 hidden md:block"
            aria-hidden="true"
          />
        </motion.div>
        <div className="relative z-10 mx-auto grid w-full max-w-[1600px] items-center gap-12 px-6 md:px-12 lg:grid-cols-12">
          <motion.div
            style={{ opacity: textOpacity, y: textY }}
            className="relative z-10 lg:col-span-7"
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              data-testid="hero-eyebrow"
              className="font-mono text-[11px] tracking-[0.28em] text-ash"
            >
              01 · ORIGIN
              <span className="mx-4 text-line">/</span>
              MEET YOUR DIGITAL TWIN
            </motion.p>

            <h1
              data-testid="hero-headline"
              className="mt-10 font-display text-5xl font-light leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl"
            >
              <MaskedLine delay={0.35}>A model of you</MaskedLine>
              <MaskedLine delay={0.5}>
                that <span className="text-signal">learns</span>
              </MaskedLine>
              <MaskedLine delay={0.65}>as you live.</MaskedLine>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-12 flex items-center gap-8"
            >
              <a
                href="https://xsdfem-1j.myshopify.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta"
                className="bg-white px-8 py-3.5 font-mono text-[11px] tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white"
              >
                GET TRIFIT
              </a>
              <span className="font-mono text-[10px] tracking-[0.2em] text-ash">
                SCROLL ↓
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.5 }}
              data-testid="hero-live-signal"
              className="mt-14 flex items-center gap-4"
            >
              <LiveSignal className="h-8 w-44" color="#D2378B" />
              <span className="font-mono text-[9px] tracking-[0.22em] text-ash">
                LIVE · TWIN SIGNAL
              </span>
            </motion.div>
          </motion.div>

        </div>

        <motion.div
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1.4, delay: 1.4 }}
          className="absolute bottom-0 left-1/2 h-16 w-px origin-top bg-white/20"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
